// Graph-backed assumptions surface (step 3). Renders the project's `assumption`
// nodes on a 2×2 board (importance × certainty) and generates more via one
// bounded Bedrock call. Each assumption is an `assumption` node on the value
// graph; edits go through the generic node CRUD, and its cell IS its
// classification (the risk level is derived from the two dimensions). An
// AI-flagged-impossible assumption isn't classified at all — the board itself
// surfaces those above the grid, so this view doesn't duplicate that warning.

import { useCallback, useMemo, useState } from 'react';
import { Loader2, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { useCanvasData } from '@/hooks/use-canvas-data';
import { useGenerateAssumptions } from '@/hooks/use-assumptions';
import { useUIStore } from '@/stores';
import { toast } from '@/hooks/use-toast';
import { GENERATION_THRESHOLDS, thresholdFriction } from '@/lib/generation-friction';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { StepHeader } from '@/features/project/StepHeader';
import { AssumptionBoard } from './AssumptionBoard';
import { AddAssumptionButton } from './AddAssumptionForm';

interface AssumptionsViewProps {
  projectId: string;
}

export function AssumptionsView({ projectId }: AssumptionsViewProps) {
  const { data: project } = useProject(projectId);
  const { data: canvas } = useCanvasData(projectId);
  const updateProject = useUpdateProject();
  const generate = useGenerateAssumptions();
  const canEdit = useUIStore((s) => s.canEdit);

  const reviewed = !!project?.assumptionsReviewed;

  const assumptions = useMemo(
    () => (canvas?.nodes ?? []).filter((n) => n.type === 'assumption'),
    [canvas?.nodes],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const friction = thresholdFriction(assumptions.length, GENERATION_THRESHOLDS.assumptions);
  // Prefer the project's canonical idea-node pointer over the canvas node:
  // the canvas filters out idea nodes with an empty title, so deriving it from
  // canvas.nodes alone would be undefined in that (supported) state and leave
  // manually-added assumptions orphaned. Fall back to the visible node id.
  const ideaNodeId = useMemo(
    () => project?.ideaNodeId ?? (canvas?.nodes ?? []).find((n) => n.type === 'idea')?.id,
    [project?.ideaNodeId, canvas?.nodes],
  );

  const { mutateAsync: generateAssumptions } = generate;
  const handleGenerate = useCallback(async () => {
    try {
      const created = await generateAssumptions(projectId);
      toast({
        title: created.length ? `Added ${created.length} assumption${created.length === 1 ? '' : 's'}` : 'No new assumptions',
      });
    } catch (err) {
      toast({
        title: 'Generation failed',
        description: err instanceof Error ? err.message : 'Failed to generate assumptions',
        variant: 'destructive',
      });
    }
  }, [generateAssumptions, projectId]);

  const setReviewed = async (value: boolean) => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { assumptionsReviewed: value } });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Failed to update step',
        variant: 'destructive',
      });
    }
  };

  const generateDisabled = generate.isPending || !project?.idea;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH} mx-auto space-y-5`}>
        <StepHeader
          projectId={projectId}
          stepId="assumptions"
          actions={
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant={reviewed ? 'outline' : 'brand'}
                onClick={() => (friction === null ? handleGenerate() : setConfirmOpen(true))}
                disabled={generateDisabled}
              >
                {generate.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                {assumptions.length > 0 ? 'Generate more' : 'Generate assumptions'}
              </Button>
              {reviewed ? (
                <Button variant="outline" onClick={() => setReviewed(false)} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : assumptions.length > 0 ? (
                <Button variant="outline" onClick={() => setReviewed(true)} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {!project?.idea && (
          <p className="text-sm text-muted-foreground">Capture your idea in step 1 to generate assumptions.</p>
        )}

        {generate.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {generate.streamedAssumptions.length > 0
              ? `Placing assumptions on the board. ${generate.streamedAssumptions.length} so far.`
              : generate.streamStarted
              ? 'Drafting the first assumption.'
              : 'Reading your idea and research.'}
          </p>
        )}

        {/* Above the board, matching the market research step: the toolbar is
            a control for the thing below it, and the board is tall enough that
            a row underneath reads as a footnote to the last column rather than
            an action on the whole step. */}
        <div className="flex items-center justify-between gap-3">
          <AddAssumptionButton
            projectId={projectId}
            ideaNodeId={ideaNodeId}
            disabled={!canEdit || !project?.idea || !ideaNodeId}
            readOnly={!canEdit}
          />
          {assumptions.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {assumptions.length} assumption{assumptions.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {assumptions.length === 0 && !generate.isPending ? (
          <div className="rounded-lg border bg-card p-12 text-center">
            <Sparkles className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <h2 className="font-semibold">No assumptions yet</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Generate them from your idea and research, or add one manually. You place each one by how much
              rides on it and how sure you are.
            </p>
          </div>
        ) : (
          <AssumptionBoard
            projectId={projectId}
            assumptions={assumptions}
            canEdit={canEdit}
            streamed={generate.isPending ? generate.streamedAssumptions : undefined}
            draftingTitle={generate.isPending ? generate.draftingTitle : null}
          />
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Your list is already long"
        body={`You have ${friction ?? 0} assumptions already. New ones will bury the ones you haven't reviewed.`}
        confirmLabel="Generate anyway"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleGenerate();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
